import {
  IconButton,
  InputAdornment,
  InputBaseComponentProps,
  TextField,
  TextFieldProps,
  useForkRef,
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { FocusEvent, useEffect, useRef, useState } from 'react';

export type ObscurableFieldProps = {
  /*For resetting the visiblity icon */
  resetVisibility?: boolean;
  /**
   * obscurePattern: substring or regexp to obscure defaults to /./g
   */
  obscurePattern?: string | RegExp;
  /**
   * obscureReplacer: replacement text string or function used to return replacement text see
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/replace#Specifying_a_function_as_a_parameter
   **/
  obscureReplacer?: string | ((substring: string, ...args: Array<string | number>) => string);
} & TextFieldProps;

/**
 * Component that handles obscuring the input
 */
function ObscurableInput({
  isVisible,
  pattern = /./g,
  replacer = '•',
  inputRef,
  onChange,
  inputComponent: InputComponent = 'input',
  onVisibilityChange,
  ...props
}: InputBaseComponentProps & { onVisibilityChange?: (visible: boolean) => void }) {
  const innerRef = useRef<HTMLInputElement | null>(null);
  const displayRef = useRef<HTMLInputElement | null>(null);
  const handleRef = useForkRef(innerRef, inputRef);

  // Check if value represents an empty/placeholder state
  const isEmptyValue = (val: string | undefined | null): boolean => {
    if (!val || val === '') return true;
    // Check for placeholder patterns like ___-__-____ or __-_______
    if (/^[_-]+$/.test(val)) return true;
    return false;
  };

  const forwardedInputRef = (el: HTMLInputElement | null) => {
    innerRef.current = el;
    if (typeof inputRef === 'function') {
      (inputRef as (instance: HTMLInputElement | null) => void)(el);
    } else if (inputRef && typeof inputRef === 'object') {
      (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
    }
  };

  const getActualInput = (): HTMLInputElement | null => {
    if (innerRef.current) {
      return innerRef.current;
    }
    if (displayRef.current) {
      const parent = displayRef.current.parentElement;
      if (parent) {
        const inputs = Array.from(parent.querySelectorAll('input')) as HTMLInputElement[];
        return inputs.find((el) => el !== displayRef.current) || null;
      }
    }
    return null;
  };

  const focusActualInput = () => {
    const el = getActualInput();
    if (el) el.focus();
  };

  // Force cursor to correct position with multiple retries
  const forceCursorPosition = (input: HTMLInputElement) => {
    if (!input) return;

    const setCorrectPosition = () => {
      try {
        const inputVal = input.value || '';
        if (isEmptyValue(inputVal) || isEmptyValue(props.value as string)) {
          input.setSelectionRange(0, 0);
        } else {
          const pos = inputVal.indexOf('_');
          if (pos !== -1) {
            input.setSelectionRange(pos, pos);
          }
        }
      } catch (e) {
        // Ignore errors if element is not focusable
      }
    };

    // Immediate
    setCorrectPosition();

    // After microtask queue (before setTimeout)
    Promise.resolve().then(setCorrectPosition);

    // Multiple setTimeout retries at different intervals
    [0, 1, 2, 5, 10, 20, 50, 100, 150, 200].forEach((delay) => {
      setTimeout(setCorrectPosition, delay);
    });

    // Also use requestAnimationFrame
    requestAnimationFrame(() => {
      setCorrectPosition();
      requestAnimationFrame(setCorrectPosition);
    });
  };

  const handleDisplayInteraction = () => {
    if (onVisibilityChange) {
      onVisibilityChange(true);
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        focusActualInput();
        const input = getActualInput();
        if (input) {
          forceCursorPosition(input);
        }
      });
    });
  };

  const handleDisplayMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    handleDisplayInteraction();
  };

  const handleActualInputFocus = (e: any) => {
    if (onVisibilityChange) {
      onVisibilityChange(true);
    }
    const input = e.target as HTMLInputElement;
    if (input) {
      forceCursorPosition(input);
    }
  };

  return (
    <>
      <input
        ref={displayRef}
        style={{ ...(isVisible && { height: '0', width: '0', position: 'absolute' }) }}
        {...props}
        aria-label="obscurable-display"
        onFocus={handleDisplayInteraction}
        onMouseDown={handleDisplayMouseDown}
        value={
          isEmptyValue(props.value as string)
            ? ''
            : (props.value || innerRef?.current?.value || '').replace(pattern, replacer)
        }
        readOnly
        tabIndex={isVisible ? -1 : undefined}
      />
      <InputComponent
        {...props}
        value={isEmptyValue(props.value as string) ? '' : props.value}
        aria-label="obscurable-input"
        style={{ ...(!isVisible && { height: '0', width: '0', position: 'absolute' }) }}
        onChange={onChange}
        ref={handleRef}
        inputRef={forwardedInputRef}
        onFocus={handleActualInputFocus}
        onKeyUp={(e: any) => {
          if (e.keyCode === 9) {
            const input = e.target as HTMLInputElement;
            if (input) forceCursorPosition(input);
          }
        }}
        onClick={(e: any) => {
          const input = e.target as HTMLInputElement;
          if (input) forceCursorPosition(input);
        }}
      />
    </>
  );
}

export function ObscurableField({
  obscurePattern = /./g,
  obscureReplacer = '•',
  onFocus = () => undefined,
  onBlur = () => undefined,
  InputProps = {},
  resetVisibility = false,
  disabled,
  ...props
}: ObscurableFieldProps): JSX.Element {
  const [isVisible, setVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (resetVisibility) {
      setVisible(false);
    }
  }, [resetVisibility]);
  const toggleVisible = () => setVisible(!isVisible);

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setVisible(true);
    onFocus(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const relatedTarget = event.relatedTarget as Node | null;
    if (containerRef.current && relatedTarget && containerRef.current.contains(relatedTarget)) {
      return;
    }
    setVisible(false);
    onBlur(event);
  };

  return (
    <div ref={containerRef}>
      <TextField
        {...props}
        variant="outlined"
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          ...InputProps,
          inputComponent: ObscurableInput,
          inputProps: {
            isVisible,
            pattern: obscurePattern,
            replacer: obscureReplacer,
            inputComponent: InputProps.inputComponent,
            onVisibilityChange: setVisible,
            ...InputProps.inputProps,
          },
          endAdornment: !disabled ? (
            <InputAdornment position="end" tabIndex={-1}>
              <IconButton size="small" tabIndex={-1} onClick={toggleVisible}>
                {!isVisible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
    </div>
  );
}
export default ObscurableField;
